import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Trophy, MessageSquare, Info, HelpCircle, Shield, FileText, Home, Gift, Users, Mail, Phone, Globe, ExternalLink, Wifi, type LucideIcon } from 'lucide-react';
import { FeedbackModal } from './FeedbackModal';
import { supabase } from '@/integrations/supabase/client';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

// Icon map for dynamic rendering
const iconMap: Record<string, LucideIcon> = {
  Info,
  HelpCircle,
  Shield,
  FileText,
  Home,
  Gift,
  Users,
  Mail,
  Phone,
  Globe,
  ExternalLink,
  MessageSquare,
};

interface FooterLink {
  label: string;
  href: string;
  icon?: string;
}

interface FooterSettings {
  brand_name: string;
  copyright_text: string;
  show_feedback_button: boolean;
  feedback_button_text: string;
  links: FooterLink[];
}

export function Footer() {
  const [showFeedback, setShowFeedback] = useState(false);
  const [settings, setSettings] = useState<FooterSettings>({
    brand_name: 'WinWGo',
    copyright_text: 'All rights reserved.',
    show_feedback_button: true,
    feedback_button_text: 'Send Feedback',
    links: [],
  });

  useEffect(() => {
    const fetchSettings = async () => {
      const { data } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', 'site_footer')
        .maybeSingle();
      
      if (data?.value && typeof data.value === 'object' && !Array.isArray(data.value)) {
        setSettings(data.value as unknown as FooterSettings);
      }
    };
    
    fetchSettings();
  }, []);

  const renderIcon = (iconName?: string) => {
    if (!iconName) return <FileText className="h-4 w-4" />;
    const IconComponent = iconMap[iconName];
    return IconComponent ? <IconComponent className="h-4 w-4" /> : <FileText className="h-4 w-4" />;
  };

  return (
    <>
      <footer className="border-t border-border/80 bg-card/50 mt-auto" role="contentinfo">
        <div className="container mx-auto px-4 sm:px-6 py-8 max-w-4xl">
          {/* Data Attribution */}
          <div className="flex items-center justify-center gap-2 mb-6 pb-6 border-b border-border/50">
            <Wifi className="h-4 w-4 text-success" />
            <span className="text-xs sm:text-sm text-muted-foreground">
              Real-time data provided by API-Football
            </span>
          </div>
          
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
            {/* Brand */}
            <div className="flex items-center gap-2.5">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary shadow-sm shadow-primary/20" aria-hidden="true">
                <Trophy className="h-4 w-4 text-primary-foreground" />
              </div>
              <span className="font-bold text-base" aria-label={`${settings.brand_name} - website brand`}>{settings.brand_name}</span>
            </div>
            
            {/* Navigation Icons */}
            <div className="flex items-center gap-1.5">
              {settings.links?.map((link, index) => (
                <Tooltip key={index}>
                  <TooltipTrigger asChild>
                    <Link
                      to={link.href}
                      className="flex items-center justify-center h-10 w-10 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                      aria-label={link.label}
                    >
                      {renderIcon(link.icon)}
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{link.label}</p>
                  </TooltipContent>
                </Tooltip>
              ))}
              {settings.show_feedback_button && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button 
                      onClick={() => setShowFeedback(true)}
                      className="flex items-center justify-center h-10 w-10 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                      aria-label={settings.feedback_button_text}
                    >
                      <MessageSquare className="h-4 w-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{settings.feedback_button_text}</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
            
            {/* Copyright */}
            <p className="text-xs text-muted-foreground">
              © {new Date().getFullYear()} {settings.brand_name}. {settings.copyright_text}
            </p>
          </div>
        </div>
      </footer>

      <FeedbackModal open={showFeedback} onOpenChange={setShowFeedback} />
    </>
  );
}
